import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, Collapse,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { assistantsApiService } from '../../services/assistantsApiService';
import { sessionService } from '../../services/sessionService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import type { AgentSessionDto } from '../../types/session';
import type { RunObject, RunStepObject } from '../../types/assistantsApi';

interface Props {
  agentId: string;
  agentSlug: string;
}

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  completed: 'success',
  in_progress: 'info',
  queued: 'info',
  requires_action: 'warning',
  failed: 'error',
  cancelled: 'default',
  cancelling: 'default',
  expired: 'error',
};

export const RunMonitor = ({ agentId, agentSlug }: Props) => {
  const [threads, setThreads] = useState<AgentSessionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedThread, setExpandedThread] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, RunObject[]>>({});
  const [runsLoading, setRunsLoading] = useState<Record<string, boolean>>({});
  const [selectedRun, setSelectedRun] = useState<RunObject | null>(null);
  const [steps, setSteps] = useState<RunStepObject[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);

  useEffect(() => {
    loadThreads();
  }, [agentId]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const result = await sessionService.getSessions({
        agentDefinitionId: agentId,
        pageSize: 100,
        searchTerm: 'Thread',
      });
      setThreads(result.items ?? []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = useCallback(async (threadId: string) => {
    const threadIdFormatted = `thread_${threadId.replace(/-/g, '')}`;
    setRunsLoading((prev) => ({ ...prev, [threadId]: true }));
    try {
      const result = await assistantsApiService.listRuns(agentSlug, threadIdFormatted);
      setRuns((prev) => ({ ...prev, [threadId]: result.data ?? [] }));
    } catch {
      setRuns((prev) => ({ ...prev, [threadId]: [] }));
    } finally {
      setRunsLoading((prev) => ({ ...prev, [threadId]: false }));
    }
  }, [agentSlug]);

  const toggleThread = (threadId: string) => {
    if (expandedThread === threadId) {
      setExpandedThread(null);
    } else {
      setExpandedThread(threadId);
      if (!runs[threadId]) {
        loadRuns(threadId);
      }
    }
  };

  const viewSteps = async (run: RunObject) => {
    setSelectedRun(run);
    setStepsLoading(true);
    try {
      const result = await assistantsApiService.listRunSteps(agentSlug, run.thread_id, run.id);
      setSteps(result.data ?? []);
    } catch {
      setSteps([]);
    } finally {
      setStepsLoading(false);
    }
  };

  const handleCancel = async (run: RunObject) => {
    try {
      await assistantsApiService.cancelRun(agentSlug, run.thread_id, run.id);
      const threadGuid = run.thread_id.replace('thread_', '');
      loadRuns(threadGuid);
    } catch (err) {
      console.error('Failed to cancel run', err);
    }
  };

  const runColumns: DataTableColumn<RunObject>[] = [
    { id: 'id', label: 'Run ID', render: (r) => <Typography variant="body2" fontFamily="monospace" fontSize={12}>{r.id}</Typography> },
    {
      id: 'status', label: 'Status',
      render: (r) => <Chip label={r.status} size="small" color={STATUS_COLORS[r.status] ?? 'default'} variant="outlined" />,
    },
    { id: 'model', label: 'Model', render: (r) => r.model },
    {
      id: 'tokens', label: 'Tokens',
      render: (r) => r.usage ? `${r.usage.prompt_tokens} / ${r.usage.completion_tokens}` : '—',
    },
    {
      id: 'created', label: 'Created',
      render: (r) => new Date(r.created_at * 1000).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View steps">
            <IconButton size="small" onClick={() => viewSteps(r)}><ViewIcon fontSize="small" /></IconButton>
          </Tooltip>
          {(r.status === 'queued' || r.status === 'in_progress' || r.status === 'requires_action') && (
            <Tooltip title="Cancel">
              <IconButton size="small" color="error" onClick={() => handleCancel(r)}><CancelIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Monitor runs across threads. Expand a thread to see its runs.
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadThreads}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Typography>Loading threads...</Typography>
      ) : threads.length === 0 ? (
        <Typography color="text.secondary">No threads found for this agent.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {threads.map((t) => (
            <Box
              key={t.id}
              sx={{
                border: '1px solid var(--glass-border)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': { background: 'var(--glass-bg-subtle)' },
                }}
                onClick={() => toggleThread(t.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {expandedThread === t.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  <Typography variant="body2" fontWeight={500}>{t.sessionName}</Typography>
                  <Chip label={`${t.messageCount ?? 0} msgs`} size="small" variant="outlined" />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(t.createdAt).toLocaleString()}
                </Typography>
              </Box>

              <Collapse in={expandedThread === t.id}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <DataTable
                    columns={runColumns}
                    rows={runs[t.id] ?? []}
                    loading={runsLoading[t.id] ?? false}
                    emptyMessage="No runs for this thread."
                    size="small"
                  />
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!selectedRun} onClose={() => setSelectedRun(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Run Steps — {selectedRun?.id}
        </DialogTitle>
        <DialogContent>
          {stepsLoading ? (
            <Typography>Loading steps...</Typography>
          ) : steps.length === 0 ? (
            <Typography color="text.secondary">No steps recorded.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {steps.map((s) => (
                <Box
                  key={s.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid var(--glass-border)',
                    background: s.type === 'tool_calls' ? 'rgba(255, 152, 0, 0.04)' : 'var(--glass-bg-subtle)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label={s.type} size="small" variant="outlined" />
                      <Chip label={s.status} size="small" color={STATUS_COLORS[s.status] ?? 'default'} variant="outlined" />
                    </Box>
                    {s.usage && (
                      <Typography variant="caption" color="text.secondary">
                        {s.usage.prompt_tokens} + {s.usage.completion_tokens} tokens
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" component="pre" sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(s.step_details, null, 2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
